import { useState, useEffect, useRef } from 'react';
import API from '../api/client';

/**
 * Champ de sélection de patient avec recherche serveur.
 * Tape ≥ 3 caractères pour rechercher par nom, prénom, CIN ou identifiant (id).
 *
 * Props:
 *  - value        : patient_id sélectionné (string|number) — '' = aucun
 *  - onChange(id) : appelé avec l'id (string) ou '' si effacé
 *  - patients     : liste déjà chargée (sert à résoudre le libellé sans appel réseau)
 *  - placeholder  : texte d'invite
 *  - allowClear   : affiche un bouton d'effacement (utile pour les filtres)
 *  - className    : classes du conteneur
 *  - testId       : data-testid
 */
export default function PatientSelect({
  value,
  onChange,
  patients = [],
  placeholder = 'Rechercher un patient (nom, CIN, id)…',
  allowClear = false,
  className = '',
  testId,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const inputClass =
    'w-full px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500';

  // Résout le libellé du patient actuellement sélectionné.
  useEffect(() => {
    if (!value) { setLabel(''); return; }
    const found = patients.find((p) => String(p.id) === String(value));
    if (found) { setLabel(`${found.prenom} ${found.nom}`); return; }
    let cancelled = false;
    API.get(`/patients/${value}`)
      .then((r) => { if (!cancelled) setLabel(`${r.data.prenom} ${r.data.nom}`); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [value, patients]);

  // Recherche serveur débattue (debounce 250 ms), à partir de 3 caractères.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 3) { setResults([]); setLoading(false); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await API.get('/patients', { params: { search: term, limit: 20 } });
        setResults(r.data.data ?? r.data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  // Ferme le menu au clic extérieur.
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (p) => {
    onChange(String(p.id));
    setLabel(`${p.prenom} ${p.nom}`);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    onChange('');
    setLabel('');
    setQuery('');
    setResults([]);
  };

  const term = query.trim();

  return (
    <div className={`relative ${className}`} ref={boxRef} data-testid={testId}>
      <div className="relative">
        <input
          type="text"
          value={open ? query : label}
          placeholder={placeholder}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          className={inputClass}
        />
        {allowClear && value && !open && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-sm"
            aria-label="Effacer"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {term.length < 3 ? (
            <div className="px-3 py-2 text-xs text-stone-400">
              Tapez au moins 3 caractères (nom, prénom, CIN ou identifiant)…
            </div>
          ) : loading ? (
            <div className="px-3 py-2 text-xs text-stone-400">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-stone-400">Aucun patient trouvé</div>
          ) : (
            results.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => select(p)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center justify-between gap-3"
              >
                <span className="font-medium text-stone-800">{p.prenom} {p.nom}</span>
                <span className="text-xs text-stone-400 whitespace-nowrap">
                  {p.CIN ? `${p.CIN} · ` : ''}#{p.id}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
